import axios from "axios";
import { auth } from "@/util/firebase";

export interface EventContext {
  url: string;
  user_agent: string;
  screen_width: number;
  screen_height: number;
  viewport_width: number;
  viewport_height: number;
}

export interface EventPayload {
  event_type: string;
  timestamp: string;
  session_id: string;
  user_id?: string;
  data: Record<string, unknown>;
  context: EventContext;
}

// Queue for batching events
let eventQueue: EventPayload[] = [];
let flushTimeout: ReturnType<typeof setTimeout> | null = null;
const FLUSH_INTERVAL_MS = 10000; // 10 seconds
const MAX_QUEUE_SIZE = 20;

// Rate limiting
let eventCount = 0;
let rateLimitResetTime = Date.now();
const MAX_EVENTS_PER_MINUTE = 100;

// Session tracking
let sessionId: string | null = null;

// Pageview duration tracking
let lastPagePath: string | null = null;
let lastPageTimestamp: number | null = null;

const enableConsoleLogs =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_BROWSER_CONSOLE_LOGS === "true";
const enableEventLogging = import.meta.env.VITE_ENABLE_EVENT_LOGGING === "true";

function getSessionId(): string {
  if (sessionId) return sessionId;

  // Try to get from sessionStorage first
  const stored = sessionStorage.getItem("ts_session_id");
  if (stored) {
    sessionId = stored;
    return sessionId;
  }

  // Generate new session ID
  sessionId = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
  sessionStorage.setItem("ts_session_id", sessionId);
  return sessionId;
}

function isRateLimited(): boolean {
  const now = Date.now();
  if (now - rateLimitResetTime > 60000) {
    eventCount = 0;
    rateLimitResetTime = now;
  }
  return eventCount >= MAX_EVENTS_PER_MINUTE;
}

function getContext(): EventContext {
  return {
    url: window.location.href,
    user_agent: navigator.userAgent,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
  };
}

async function flushEvents() {
  if (eventQueue.length === 0) return;

  const eventsToSend = [...eventQueue];
  eventQueue = [];
  flushTimeout = null;

  try {
    await axios.post(
      "/v2/events/log",
      { events: eventsToSend },
      {
        validateStatus: () => true,
        timeout: 5000,
      }
    );
  } catch {
    if (enableConsoleLogs) {
      console.warn("[EventLogger] Failed to send events to server");
    }
  }
}

function scheduleFlush() {
  if (flushTimeout) return;
  flushTimeout = setTimeout(flushEvents, FLUSH_INTERVAL_MS);
}

function queueEvent(payload: EventPayload) {
  if (isRateLimited()) {
    if (enableConsoleLogs) {
      console.warn("[EventLogger] Rate limited, skipping event");
    }
    return;
  }

  eventCount++;
  eventQueue.push(payload);

  if (eventQueue.length >= MAX_QUEUE_SIZE) {
    flushEvents();
  } else {
    scheduleFlush();
  }
}

/**
 * Log a generic event to the backend
 * @param eventType - Type of event (e.g., "button_click", "feature_used")
 * @param data - Event-specific data
 */
export function logEvent(
  eventType: string,
  data: Record<string, unknown> = {}
): void {
  // Skip in development unless explicitly enabled
  if (import.meta.env.DEV && !enableEventLogging) {
    if (enableConsoleLogs) console.log("[EventLogger]", eventType, data);
    return;
  }

  const payload: EventPayload = {
    event_type: eventType,
    timestamp: new Date().toISOString(),
    session_id: getSessionId(),
    user_id: auth.currentUser?.uid,
    data,
    context: getContext(),
  };

  queueEvent(payload);
}

/**
 * Log a pageview event with duration tracking
 * @param path - The page path
 */
export function logPageview(path: string): void {
  const now = Date.now();
  const referrer = lastPagePath || document.referrer || "";

  // Calculate duration on previous page
  let durationMs: number | undefined;
  if (lastPageTimestamp) {
    durationMs = now - lastPageTimestamp;
  }

  // Determine navigation type
  let navigationType: "initial" | "internal" | "external" = "initial";
  if (lastPagePath) {
    navigationType = "internal";
  } else if (document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer);
      if (referrerUrl.origin !== window.location.origin) {
        navigationType = "external";
      }
    } catch {
      // Invalid URL
    }
  }

  // Update tracking for next pageview
  lastPagePath = path;
  lastPageTimestamp = now;

  logEvent("pageview", {
    path,
    referrer,
    duration_ms: durationMs,
    navigation_type: navigationType,
  });
}

/**
 * Log a button click event
 * @param buttonId - Identifier for the button
 * @param additionalData - Optional additional data
 */
export function logButtonClick(
  buttonId: string,
  additionalData: Record<string, unknown> = {}
): void {
  logEvent("button_click", {
    button_id: buttonId,
    page: window.location.pathname,
    ...additionalData,
  });
}

/**
 * Log a feature usage event
 * @param feature - Name of the feature
 * @param action - Action performed (e.g., "opened", "used", "completed")
 * @param additionalData - Optional additional data
 */
export function logFeatureUsed(
  feature: string,
  action: string,
  additionalData: Record<string, unknown> = {}
): void {
  logEvent("feature_used", {
    feature,
    action,
    page: window.location.pathname,
    ...additionalData,
  });
}

/**
 * Initialize the event logger - call once at app startup
 */
export function initEventLogger(): void {
  // Initialize session ID
  getSessionId();

  // Flush events before page unload
  window.addEventListener("beforeunload", () => {
    if (eventQueue.length > 0) {
      const payload = JSON.stringify({ events: eventQueue });
      const baseUrl = import.meta.env.VITE_API_V2_BASE_URL || "";
      navigator.sendBeacon(`${baseUrl}/v2/events/log`, payload);
    }
  });

  // Handle visibility change (user switches tabs)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushEvents();
    }
  });

  if (enableConsoleLogs) {
    console.log("[EventLogger] Initialized with session:", getSessionId());
  }
}
